import React, { useState, useRef } from 'react';
import { FileText, Image, MoreHorizontal, X, ArrowDownToLine, ExternalLink, Trash2, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { FileAttachment as FileAttachmentType } from '../../shared/types/types';

interface FileAttachmentProps {
  attachment: FileAttachmentType;
  onRemove?: () => void;
  isDraft?: boolean;
  showDownload?: boolean;
}

const getFileIcon = (fileName: string) => {
  const extension = fileName.split('.').pop()?.toLowerCase();
  
  if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'tiff', 'tif'].includes(extension || '')) {
    return <Image className="w-3 h-3 text-slate-500" strokeWidth={1.5} />;
  }
  
  return <FileText className="w-3 h-3 text-slate-500" strokeWidth={1.5} />;
};

const getFileSize = (): string => {
  // Since we don't have file size from the data, we'll show a placeholder
  // In a real implementation, you might want to fetch this or store it in Airtable
  return '';
};

const convertDriveUrl = (url: string): { directUrl: string; previewUrl: string } => {
  // Convert Google Drive share URL to direct download and preview URLs
  const fileIdMatch = url.match(/\/file\/d\/([a-zA-Z0-9_-]+)/);
  if (fileIdMatch) {
    const fileId = fileIdMatch[1];
    return {
      directUrl: `https://drive.google.com/uc?export=download&id=${fileId}`,
      previewUrl: `https://drive.google.com/file/d/${fileId}/preview`
    };
  }
  return { directUrl: url, previewUrl: url };
};

export const FileAttachment: React.FC<FileAttachmentProps> = ({ attachment, onRemove, isDraft = false, showDownload = true }) => {
  const { t } = useTranslation();
  const [showMenu, setShowMenu] = useState(false);
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState<{
    left: number;
    maxWidth: number;
    shouldWrap: boolean;
  }>({ left: 0, maxWidth: 300, shouldWrap: false });

  const tooltipTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const { directUrl, previewUrl } = convertDriveUrl(attachment.url);

  const handleDownload = () => {
    window.open(directUrl, '_blank');
    setShowMenu(false);
  };

  const handlePreview = () => {
    window.open(previewUrl, '_blank');
    setShowMenu(false);
  };

  const handleCardClick = () => {
    // Don't open preview for draft files
    if (isDraft) {
      return;
    }
    
    if (isPdfFile(attachment.name)) {
      setShowPdfModal(true);
    }
    // For non-PDF files, clicking the card does nothing (only the dropdown menu works)
  };

  const handleMenuToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowMenu(!showMenu);
  };

  const handleMenuClose = () => {
    setShowMenu(false);
  };

  const handleRemove = () => {
    if (onRemove) {
      onRemove();
    }
    setShowMenu(false);
  };

  const calculateTooltipPosition = () => {
    if (!containerRef.current) return;
    
    const container = containerRef.current;
    const containerRect = container.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    
    // Constants for spacing
    const MARGIN = 16; // 1rem margin from edges
    const MIN_WIDTH = 150; // Minimum tooltip width
    const MAX_WIDTH = 300; // Maximum tooltip width for single line
    
    // Calculate available space on the right
    const spaceOnRight = viewportWidth - containerRect.left - MARGIN;
    
    // Estimate text width (rough approximation: 7px per character)
    const estimatedTextWidth = attachment.name.length * 7 + 16; // +16 for padding
    
    let left = 0;
    let maxWidth = MAX_WIDTH;
    let shouldWrap = false;
    
    // If estimated text width fits comfortably in available space
    if (estimatedTextWidth <= spaceOnRight && estimatedTextWidth <= MAX_WIDTH) {
      // Use left alignment, no wrapping needed
      left = 0;
      maxWidth = Math.max(estimatedTextWidth, MIN_WIDTH);
    } else if (spaceOnRight >= MIN_WIDTH) {
      // Use available space on the right with wrapping
      left = 0;
      maxWidth = Math.min(spaceOnRight - MARGIN, MAX_WIDTH);
      shouldWrap = estimatedTextWidth > maxWidth;
    } else {
      // Not enough space on right, try centering with wrapping
      const spaceOnLeft = containerRect.left - MARGIN;
      const totalAvailableSpace = Math.max(spaceOnLeft, spaceOnRight);
      
      if (totalAvailableSpace >= MIN_WIDTH) {
        left = Math.min(0, -(totalAvailableSpace - container.offsetWidth) / 2);
        maxWidth = Math.min(totalAvailableSpace, MAX_WIDTH);
        shouldWrap = estimatedTextWidth > maxWidth;
      } else {
        // Very constrained space, use minimum width with wrapping
        left = 0;
        maxWidth = MIN_WIDTH;
        shouldWrap = true;
      }
    }
    
    setTooltipPosition({ left, maxWidth, shouldWrap });
  };

  const handleTooltipMouseEnter = () => {
    // Clear any existing timeout
    if (tooltipTimeoutRef.current) {
      clearTimeout(tooltipTimeoutRef.current);
    }
    // Calculate position first
    calculateTooltipPosition();
    // Set timeout to show tooltip after 500ms
    tooltipTimeoutRef.current = setTimeout(() => {
      setShowTooltip(true);
    }, 500);
  };

  const handleTooltipMouseLeave = () => {
    // Clear timeout if mouse leaves before tooltip appears
    if (tooltipTimeoutRef.current) {
      clearTimeout(tooltipTimeoutRef.current);
      tooltipTimeoutRef.current = null;
    }
    // Hide tooltip immediately
    setShowTooltip(false);
  };

  return (
    <div className="relative inline-block" ref={containerRef}>
      <div 
        className={`flex items-center gap-2 bg-white border border-slate-200 px-2.5 py-1.5 w-48 hover:bg-slate-50 transition-colors ${
          !isDraft && isPdfFile(attachment.name) ? 'cursor-pointer' : ''
        }`}
        onClick={handleCardClick}
        onMouseEnter={handleTooltipMouseEnter}
        onMouseLeave={handleTooltipMouseLeave}
      >
        {getFileIcon(attachment.name)}
        <div className="flex-1 min-w-0">
          <div className="text-xs text-slate-800 truncate font-normal">
            {attachment.name}
          </div>
          {getFileSize() && (
            <div className="text-xs text-slate-500">
              {getFileSize()}
            </div>
          )}
        </div>
        <button
          onClick={handleMenuToggle}
          className="p-0.5 hover:bg-slate-200 transition-colors flex-shrink-0"
        >
          <MoreHorizontal className="w-3 h-3 text-slate-400" strokeWidth={1.5} />
        </button>
      </div>

      {/* Dropdown Menu */}
      {showMenu && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 z-10" 
            onClick={handleMenuClose}
          />
          
          {/* Menu */}
          <div className="absolute right-0 top-full mt-1 w-44 bg-white border border-slate-200 shadow-lg z-20">
            <div className="py-1">
              <button
                onClick={handlePreview}
                className="w-full px-3 py-2.5 text-left text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2.5 transition-colors"
              >
                <ExternalLink className="w-3.5 h-3.5" strokeWidth={1.5} />
                {t('files.preview')}
              </button>
              {showDownload && (
                <button
                  onClick={handleDownload}
                  className="w-full px-3 py-2.5 text-left text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2.5 transition-colors"
                >
                  <ArrowDownToLine className="w-3.5 h-3.5" strokeWidth={1.5} />
                  {t('files.download')}
                </button>
              )}
              {onRemove && (
                <button
                  onClick={handleRemove}
                  className="w-full px-3 py-2.5 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2.5 transition-colors"
                >
                  <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                  {t('files.deleteFile')}
                </button>
              )}
            </div>
          </div>
        </>
      )}

      {/* Filename Tooltip */}
      {showTooltip && (
        <div 
          className={`absolute bottom-full mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded z-30 shadow-lg ${
            tooltipPosition.shouldWrap ? 'whitespace-normal break-words' : 'whitespace-nowrap'
          }`}
          style={{
            left: `${tooltipPosition.left}px`,
            maxWidth: `${tooltipPosition.maxWidth}px`,
            minWidth: '150px'
          }}
        >
          {attachment.name}
          <div className="absolute top-full left-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-gray-900"></div>
        </div>
      )}

      {/* PDF Preview Modal */}
      <PDFModal
        pdf={attachment}
        isOpen={showPdfModal}
        onClose={() => setShowPdfModal(false)}
      />
    </div>
  );
};

// Image popup modal component
const ImageModal: React.FC<{
  images: FileAttachmentType[];
  isOpen: boolean;
  onClose: () => void;
}> = ({ images, isOpen, onClose }) => {
  const { t } = useTranslation();

  // Prevent body scroll when modal is open
  React.useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={onClose} // Close when clicking backdrop
    >
      <div
        className="bg-white max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()} // Prevent closing when clicking inside modal
      >
        <div className="flex items-center justify-between p-4 border-b border-slate-200 sticky top-0 bg-white z-10">
          <div>
            <h3 className="text-lg font-medium text-slate-900">{t('files.images')}</h3>
            <p className="text-sm text-slate-500 mt-1">{t('files.imagesSubtitle')}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-100 transition-colors flex-shrink-0"
          >
            <X className="w-5 h-5 text-slate-500" strokeWidth={1.5} />
          </button>
        </div>
        <div className="p-4 pb-32">
          <div className="grid grid-cols-2 gap-3">
            {images.map((image, index) => (
              <FileAttachment
                key={`image-${image.name}-${index}`}
                attachment={image}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// PDF preview modal component
const PDFModal: React.FC<{
  pdf: FileAttachmentType;
  isOpen: boolean;
  onClose: () => void;
}> = ({ pdf, isOpen, onClose }) => {
  const { t } = useTranslation();

  // Prevent body scroll when modal is open
  React.useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  // Convert Google Drive URL to embeddable format if needed
  const { previewUrl } = convertDriveUrl(pdf.url);
  const embedUrl = previewUrl.includes('drive.google.com')
    ? previewUrl.replace('/preview', '/preview?embedded=true')
    : previewUrl;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2"
      onClick={onClose} // Close when clicking backdrop
    >
      <div
        className="bg-white max-w-6xl w-full h-[96vh] flex flex-col shadow-2xl"
        onClick={(e) => e.stopPropagation()} // Prevent closing when clicking inside modal
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-200 flex-shrink-0">
          <div className="flex items-center gap-3">
            <FileText className="w-5 h-5 text-slate-500" strokeWidth={1.5} />
            <div>
              <h3 className="text-lg font-medium text-slate-900">{pdf.name}</h3>
              <p className="text-sm text-slate-500">{t('files.pdfPreview')}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                const { directUrl } = convertDriveUrl(pdf.url);
                window.open(directUrl, '_blank');
              }}
              className="px-3 py-1.5 text-sm bg-slate-100 hover:bg-slate-200 text-slate-700 transition-colors flex items-center gap-2"
            >
              <ArrowDownToLine className="w-4 h-4" strokeWidth={1.5} />
              {t('files.download')}
            </button>
            <button
              onClick={() => window.open(previewUrl, '_blank')}
              className="px-3 py-1.5 text-sm bg-slate-100 hover:bg-slate-200 text-slate-700 transition-colors flex items-center gap-2"
            >
              <ExternalLink className="w-4 h-4" strokeWidth={1.5} />
              {t('files.openInNewTab')}
            </button>
            <button
              onClick={onClose}
              className="p-2 hover:bg-slate-100 transition-colors flex-shrink-0"
            >
              <X className="w-5 h-5 text-slate-500" strokeWidth={1.5} />
            </button>
          </div>
        </div>

        {/* PDF Viewer */}
        <div className="flex-1 min-h-0">
          <iframe
            src={embedUrl}
            className="w-full h-full border-0"
            title={`${t('files.pdfPreview')} - ${pdf.name}`}
            sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
          />
        </div>
      </div>
    </div>
  );
};

interface FileAttachmentsListProps {
  attachments: FileAttachmentType[];
  processingFiles?: Array<{id: string; filename: string; status: 'processing'}>;
}

const isImageFile = (fileName: string): boolean => {
  const extension = fileName.split('.').pop()?.toLowerCase();
  return ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'tiff', 'tif'].includes(extension || '');
};

const isPdfFile = (fileName: string): boolean => {
  const extension = fileName.split('.').pop()?.toLowerCase();
  return extension === 'pdf';
};

export const FileAttachmentsList: React.FC<FileAttachmentsListProps> = ({
  attachments,
  processingFiles
}) => {
  const { t } = useTranslation();
  const [showImageModal, setShowImageModal] = useState(false);

  // Filter out attachments with empty/invalid URLs
  const validAttachments = attachments?.filter(att => att.url && att.url.trim() !== '') || [];


  // Show processing files indicator if no valid attachments but files are processing
  if (validAttachments.length === 0 && processingFiles && processingFiles.length > 0) {
    return (
      <div className="mt-3">
        <div className="text-xs text-slate-500 mb-2 flex items-center gap-2">
          <Loader2 className="w-3 h-3 animate-spin" />
          <span>{t('files.emailSent')}</span>
        </div>
        <div className="space-y-1">
          {processingFiles.map((file) => (
            <div key={file.id} className="flex items-center gap-2 text-xs text-slate-600">
              <svg className="w-3 h-3 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
              </svg>
              <span className="opacity-70">{file.filename}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }
  
  if (validAttachments.length === 0) {
    return null;
  }

  // Separate images from other files - use validAttachments 
  const documentFiles = validAttachments.filter(file => !isImageFile(file.name));
  const imageFiles = validAttachments.filter(file => isImageFile(file.name));

  return (
    <div className="mt-3">
      {/* Show document files */}
      {documentFiles.length > 0 && (
        <div className="mb-3">
          <div className="text-xs text-slate-400 mb-2.5 flex items-center gap-3">
            <span>{documentFiles.length} {documentFiles.length === 1 ? t('files.document') : t('files.documents')}</span>
            {imageFiles.length > 0 && (
              <button
                onClick={() => setShowImageModal(true)}
                className="text-slate-500 hover:text-slate-700 underline underline-offset-2 transition-colors"
              >
                + {imageFiles.length} {imageFiles.length === 1 ? t('files.image') : t('files.images_plural')}
              </button>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {documentFiles.map((attachment, index) => (
              <FileAttachment
                key={`doc-${attachment.name}-${index}`}
                attachment={attachment}
              />
            ))}
          </div>
        </div>
      )}

      {/* Show "more files" button for images when no documents */}
      {imageFiles.length > 0 && documentFiles.length === 0 && (
        <div>
          <button
            onClick={() => setShowImageModal(true)}
            className="text-xs text-slate-500 hover:text-slate-700 underline underline-offset-2 transition-colors"
          >
            + {imageFiles.length} {imageFiles.length === 1 ? t('files.image') : t('files.images_plural')}
          </button>
        </div>
      )}

      {/* Image modal */}
      <ImageModal
        images={imageFiles}
        isOpen={showImageModal}
        onClose={() => setShowImageModal(false)}
      />
    </div>
  );
};